import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, Landmark, Building2 } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Kicker } from "../components/Reveal";
import { CALENDLY_URL, TRUST_SERVICES, FAQS } from "../data/content";
import { Faq } from "../components/Faq";

const AUDIENCES = [
  {
    icon: Landmark,
    title: "Existing Trust Companies",
    body: "Independent and bank-affiliated trust companies seeking to strengthen governance, prepare for examinations, modernize operations, or optimize technology and the digital client experience.",
  },
  {
    icon: Building2,
    title: "RIAs Evaluating a Trust Company",
    body: "Registered investment advisers weighing whether to form a trust company — or affiliate with one — and looking for an objective, end-to-end evaluation before committing capital and reputation.",
  },
];

const PROCESS = [
  { step: "Evaluate", body: "Feasibility and strategic assessment: market, capital, jurisdiction, and organizational impact." },
  { step: "Prepare", body: "Charter readiness — business plans, policies, pro formas, and regulator-facing documentation." },
  { step: "Establish", body: "Governance frameworks, fiduciary compliance programs, and operational infrastructure." },
  { step: "Sustain", body: "Examination readiness, operations oversight, and technology and digital optimization over time." },
];

const TrustServices = () => (
  <div data-testid="trust-services-page">
    <PageHero
      kicker="Trust Services Consulting"
      lines={[<span key="a">Build the fiduciary</span>, <span key="b">future with <em className="italic text-[#3E9BDC]">confidence.</em></span>]}
      intro="Whether you operate a trust company today or are an RIA evaluating whether to form — or affiliate with — one, American Elm provides practical, examiner-ready guidance across the full fiduciary lifecycle."
      meta={["Feasibility", "Charter Readiness", "Governance", "Fiduciary Compliance", "Examinations", "Operations", "Technology"]}
    />

    <section className="py-20 md:py-28" data-testid="trust-audiences">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker>Who We Serve</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight max-w-3xl leading-[1.15]">
            Two audiences. One disciplined methodology.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.07}>
              <div className="h-full p-8 md:p-12 bg-[#EBF3F8] border border-[#A9CFE2]/50" data-testid={`trust-audience-${i}`}>
                <a.icon size={28} className="text-[#3E9BDC]" />
                <h3 className="mt-5 font-serif font-bold text-2xl text-[#0A2B3D]">{a.title}</h3>
                <p className="mt-4 text-[#475569] leading-relaxed">{a.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 md:py-28 border-t border-[#0A2B3D]/10" data-testid="trust-capabilities">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker>Capabilities</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight max-w-3xl leading-[1.15]">
            Seven disciplines, from first question to ongoing oversight.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {TRUST_SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div
                className={`h-full p-8 md:p-10 bg-white border border-[#0A2B3D]/12 hover:border-[#3E9BDC]/60 transition-colors duration-500 ${i === TRUST_SERVICES.length - 1 ? "md:col-span-2" : ""}`}
                data-testid={`trust-service-${i}`}
              >
                <h3 className="font-serif font-bold text-xl md:text-2xl text-[#0A2B3D]">{s.title}</h3>
                <p className="mt-4 text-sm text-[#475569] leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#0A2B3D] text-white py-20 md:py-28" data-testid="trust-process">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker light>The Engagement Path</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl tracking-tight max-w-2xl leading-[1.15]">
            Evaluate. Prepare. Establish. Sustain.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCESS.map((p, i) => (
            <Reveal key={p.step} delay={i * 0.08}>
              <div className="h-full border border-white/15 p-8 bg-[#082838] hover:border-[#3E9BDC]/60 transition-colors duration-500" data-testid={`trust-step-${i}`}>
                <h3 className="font-serif font-bold text-2xl">{p.step}</h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-12">
          <p className="text-white/60 text-sm max-w-3xl leading-relaxed" data-testid="trust-outcomes-note">
            The outcome: a clear, evidence-based answer on whether a trust charter serves your
            clients and your firm — and, if so, an organization built to satisfy examiners,
            serve beneficiaries, and grow.
          </p>
        </Reveal>
      </div>
    </section>

    <Faq items={FAQS.trust} id="trust-faq" />

    <div className="max-w-4xl mx-auto px-5 sm:px-8 -mt-6 md:-mt-10 pb-4" data-testid="trust-faq-explore-more">
      <Link to="/trust-services-faq" data-testid="trust-explore-more-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#3E9BDC] hover:text-[#0A2B3D] transition-colors underline underline-offset-4">
        Explore more trust services questions <ArrowRight size={15} />
      </Link>
    </div>

    <section className="bg-[#A9CFE2] py-16 md:py-24" data-testid="trust-cta">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <div>
          <h2 className="font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight">Explore a trust strategy.</h2>
          <p className="mt-3 text-[#0A2B3D]/75 max-w-xl">A candid conversation about feasibility is the right place to start.</p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
          <Link to="/contact?topic=Trust%20Services%20Consulting" data-testid="trust-cta-button" className="btn-primary">
            Request a Consultation <ArrowRight size={16} />
          </Link>
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="trust-calendly-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2B3D] hover:text-[#082838] underline underline-offset-4">
            <CalendarClock size={16} /> Or schedule a 15-minute call
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default TrustServices;
